/* eslint-disable max-lines-per-function */
import { Arbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/Arbeitsbereich';
import { KalenderWoche } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/KalenderWoche';
import { RowDivider } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/RowDivider';
import { ArbeitsbereichKalenderStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/ArbeitsbereichKalenderStation';
import { renderRotationsplaeneByMitarbeiterRow } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/renderRotationsplaeneByMitarbeiterRow';
import {
  createKalenderScrollStore,
  KalenderScrollContext,
} from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/kalenderScrollStore';
import { useRotationsplanFeatureContext } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { CSSProperties, Fragment, RefObject, useRef } from 'react';

type StyleProps = CSSProperties & {
  '--col-start': CSSProperties['gridColumnStart'];
};

type StylePropsGrid = CSSProperties & {
  '--col-count': CSSProperties['gridTemplateColumns'];
};

export function Kalender({
  view,
  kalenderWocheRef,
}: {
  view: 'mitarbeiter' | 'arbeitsbereiche';
  kalenderWocheRef: RefObject<HTMLDivElement>;
}) {
  const {
    store: {
      data: {
        planung: { rotationsplaene, rotationsplaeneByArbeitsbereiche },
      },
      ui: { year: jahr },
    },
  } = useRotationsplanFeatureContext();
  const weeksInYear = dayjs(`${jahr}-01-01`).isoWeeksInYear();
  const gridRef = useRef<HTMLDivElement | null>(undefined);
  const scrollStoreRef =
    useRef<ReturnType<typeof createKalenderScrollStore>>(undefined);
  if (!scrollStoreRef.current) {
    scrollStoreRef.current = createKalenderScrollStore();
  }
  const scrollStore = scrollStoreRef.current;
  const endOfYear = dayjs().year(parseInt(jahr)).endOf('year');
  const startOfYear = dayjs().year(parseInt(jahr)).startOf('year');

  return (
    <KalenderScrollContext.Provider value={scrollStore}>
      <div className="w-full relative">
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-linear-to-l from-white to-transparent z-10" />
        <div
          className="overflow-x-auto"
          onScroll={() => scrollStore.getState().setIsScrolling(true)}
          onScrollEnd={() => scrollStore.getState().setIsScrolling(false)}>
          <div
            key={rotationsplaene.map((plan) => plan.id).join('-')}
            ref={gridRef}
            className={`grid grid-cols-[1fr_repeat(var(--col-count),8px)] md:grid-cols-[180px_repeat(var(--col-count),16px)] w-fit h-full gap-x-1`}
            style={
              {
                '--col-count': weeksInYear * 7,
              } as StylePropsGrid
            }>
            <div className="sticky left-0 bg-white z-10 row-start-1 border-r border-solid border-neutrals-200"></div>
            {Array.from({ length: weeksInYear }, (_, i) => {
              const startOfWeek = dayjs()
                .year(parseInt(jahr))
                .isoWeek(i + 1)
                .startOf('isoWeek');

              const endOfWeek = dayjs()
                .year(parseInt(jahr))
                .isoWeek(i + 1)
                .endOf('isoWeek');

              const isCurrentWeek =
                dayjs().get('year').toString() === jahr &&
                dayjs().isoWeek() === i + 1;

              return (
                <div
                  key={i}
                  className={`col-start-(--col-start) col-span-7 row-start-1 p-2`}
                  style={
                    {
                      '--col-start': i * 7 + 2,
                    } as StyleProps
                  }>
                  <KalenderWoche
                    isCurrentWeek={isCurrentWeek}
                    startOfWeek={startOfWeek.format('DD.MM')}
                    endOfWeek={endOfWeek.format('DD.MM')}
                    index={i}
                    gridRef={gridRef}
                    kalenderWocheRef={kalenderWocheRef}
                  />
                </div>
              );
            })}
            <RowDivider />
            {view === 'mitarbeiter'
              ? renderRotationsplaeneByMitarbeiterRow({
                  rotationsplaene,
                  endOfYear,
                  startOfYear,
                  jahr,
                  weeksInYear,
                })
              : rotationsplaeneByArbeitsbereiche.map((arbeitsbereich) => {
                  return (
                    <Fragment key={arbeitsbereich.id}>
                      <Arbeitsbereich arbeitsbereich={arbeitsbereich} />
                      {arbeitsbereich.stationen
                        .filter(
                          (kalenderStation) =>
                            dayjs(kalenderStation.station.von).isSameOrBefore(
                              endOfYear,
                            ) &&
                            dayjs(kalenderStation.station.bis).isSameOrAfter(
                              startOfYear,
                            ),
                        )
                        .map((kalenderStation, index) => (
                          <ArbeitsbereichKalenderStation
                            key={index + '_' + kalenderStation.station.id}
                            kalenderStation={kalenderStation}
                            arbeitsbereich={arbeitsbereich}
                            displayYear={parseInt(jahr)}
                          />
                        ))}
                      <RowDivider />
                    </Fragment>
                  );
                })}
          </div>
        </div>
      </div>
    </KalenderScrollContext.Provider>
  );
}
